import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const CoordinatesGuide = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-6 inline-flex items-center text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('coordinatesGuide.back', 'Назад')}
          </button>
          
          <div className="flex items-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {t('coordinatesGuide.title', 'Како да најдете GPS координати')}
              </h1>
              <p className="text-gray-600 mt-1">
                {t('coordinatesGuide.subtitle', 'Водич за пронаоѓање на прецизна локација')}
              </p>
            </div>
          </div>
        </div>

        {/* Google Maps Method */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t('coordinatesGuide.googleMaps.title', 'Од Google Maps')}
            </h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4 mt-1">
                <span className="text-indigo-600 font-bold">1</span>
              </div>
              <div>
                <p className="text-gray-700 font-semibold mb-1">
                  {t('coordinatesGuide.googleMaps.step1.title', 'Отворете Google Maps')}
                </p>
                <p className="text-gray-600">
                  {t('coordinatesGuide.googleMaps.step1.description', 'Посетете maps.google.com на вашиот компјутер или отворете апликацијата на телефон.')}
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4 mt-1">
                <span className="text-indigo-600 font-bold">2</span>
              </div>
              <div>
                <p className="text-gray-700 font-semibold mb-1">
                  {t('coordinatesGuide.googleMaps.step2.title', 'Најдете ја локацијата')}
                </p>
                <p className="text-gray-600">
                  {t('coordinatesGuide.googleMaps.step2.description', 'Пребарајте или движете ја картата до местото каде што се наоѓа гробот.')}
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4 mt-1">
                <span className="text-indigo-600 font-bold">3</span>
              </div>
              <div>
                <p className="text-gray-700 font-semibold mb-1">
                  {t('coordinatesGuide.googleMaps.step3.title', 'Кликнете или допрете на локацијата')}
                </p>
                <p className="text-gray-600 mb-2">
                  {t('coordinatesGuide.googleMaps.step3.description', 'Кликнете со десното копче (или долго допрете на телефон) на точната локација. Ќе се појави мени.')}
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                  <p className="text-sm text-amber-800">
                    <strong>{t('coordinatesGuide.tip', 'Совет')}:</strong> {t('coordinatesGuide.googleMaps.step3.tip', 'Зумирајте што е можно повеќе за да бидете попрецизни.')}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4 mt-1">
                <span className="text-indigo-600 font-bold">4</span>
              </div>
              <div>
                <p className="text-gray-700 font-semibold mb-1">
                  {t('coordinatesGuide.googleMaps.step4.title', 'Копирајте ги координатите')}
                </p>
                <p className="text-gray-600 mb-2">
                  {t('coordinatesGuide.googleMaps.step4.description', 'Во менито, кликнете на првите два броја (координатите). Тие автоматски ќе се копираат.')}
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-2 font-mono text-sm">
                  <span className="text-gray-500">{t('coordinatesGuide.example', 'Пример')}:</span> <span className="font-bold text-gray-900">41.998100, 21.425400</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Apple Maps Method */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t('coordinatesGuide.appleMaps.title', 'Од Apple Maps')}
            </h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4 mt-1">
                <span className="text-indigo-600 font-bold">1</span>
              </div>
              <div>
                <p className="text-gray-700 font-semibold mb-1">
                  {t('coordinatesGuide.appleMaps.step1.title', 'Отворете Apple Maps')}
                </p>
                <p className="text-gray-600">
                  {t('coordinatesGuide.appleMaps.step1.description', 'Отворете ја апликацијата Maps на вашиот iPhone, iPad или Mac.')}
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4 mt-1">
                <span className="text-indigo-600 font-bold">2</span>
              </div>
              <div>
                <p className="text-gray-700 font-semibold mb-1">
                  {t('coordinatesGuide.appleMaps.step2.title', 'Најдете ја локацијата')}
                </p>
                <p className="text-gray-600">
                  {t('coordinatesGuide.appleMaps.step2.description', 'Пребарајте или движете ја картата до точната локација.')}
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4 mt-1">
                <span className="text-indigo-600 font-bold">3</span>
              </div>
              <div>
                <p className="text-gray-700 font-semibold mb-1">
                  {t('coordinatesGuide.appleMaps.step3.title', 'Допрете и задржете')}
                </p>
                <p className="text-gray-600">
                  {t('coordinatesGuide.appleMaps.step3.description', 'Допрете и задржете на точната локација. Ќе се појави пин.')}
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4 mt-1">
                <span className="text-indigo-600 font-bold">4</span>
              </div>
              <div>
                <p className="text-gray-700 font-semibold mb-1">
                  {t('coordinatesGuide.appleMaps.step4.title', 'Видете ги координатите')}
                </p>
                <p className="text-gray-600">
                  {t('coordinatesGuide.appleMaps.step4.description', 'Повлечете го картончето нагоре. Координатите ќе бидат прикажани под името на локацијата. Допрете за копирање.')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Photo Metadata Method */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t('coordinatesGuide.photoMetadata.title', 'Од Фотографија')}
            </h2>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-700 mb-4">
              {t('coordinatesGuide.photoMetadata.description', 'Ако имате фотографија снимена на локацијата, можете да ги извлечете координатите од нејзините метаподатоци (EXIF).')}
            </p>

            <div className="flex items-start">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4 mt-1">
                <span className="text-indigo-600 font-bold">1</span>
              </div>
              <div>
                <p className="text-gray-700 font-semibold mb-1">
                  {t('coordinatesGuide.photoMetadata.step1.title', 'На iPhone/iPad')}
                </p>
                <p className="text-gray-600">
                  {t('coordinatesGuide.photoMetadata.step1.description', 'Отворете ја фотографијата во Photos апликацијата, повлечете нагоре, и видете ги детали вклучувајќи ја картата со локацијата.')}
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4 mt-1">
                <span className="text-indigo-600 font-bold">2</span>
              </div>
              <div>
                <p className="text-gray-700 font-semibold mb-1">
                  {t('coordinatesGuide.photoMetadata.step2.title', 'На Android')}
                </p>
                <p className="text-gray-600">
                  {t('coordinatesGuide.photoMetadata.step2.description', 'Отворете ја фотографијата, допрете на "детали" или "инфо" иконата за да ги видите метаподатоците вклучувајќи GPS координати.')}
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4 mt-1">
                <span className="text-indigo-600 font-bold">3</span>
              </div>
              <div>
                <p className="text-gray-700 font-semibold mb-1">
                  {t('coordinatesGuide.photoMetadata.step3.title', 'На компјутер')}
                </p>
                <p className="text-gray-600">
                  {t('coordinatesGuide.photoMetadata.step3.description', 'Кликнете со десното копче на фотографијата > Properties > Details, и барајте "GPS" полиња.')}
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-blue-800">
                <strong>{t('coordinatesGuide.note', 'Забелешка')}:</strong> {t('coordinatesGuide.photoMetadata.note', 'Локацијата мора да е вклучена на уредот кога фотографијата е снимена за да се зачуваат GPS координати.')}
              </p>
            </div>
          </div>
        </div>

        {/* Format Information */}
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl shadow-xl p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t('coordinatesGuide.format.title', 'Формат на Координати')}
            </h2>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-700">
              {t('coordinatesGuide.format.description', 'Координатите треба да бидат во следниов формат:')}
            </p>
            
            <div className="bg-white border-2 border-indigo-200 rounded-xl p-6">
              <p className="text-gray-600 mb-2">{t('coordinatesGuide.format.latitude', 'Географска ширина, Географска должина')}</p>
              <p className="font-mono text-2xl font-bold text-indigo-600">41.998100, 21.425400</p>
            </div>

            <ul className="space-y-2 mt-4">
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">{t('coordinatesGuide.format.rule1', 'Користете децимална точка (.), не запирка')}</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">{t('coordinatesGuide.format.rule2', 'Раздвојте ги со запирка и празно место')}</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">{t('coordinatesGuide.format.rule3', 'Прво географската ширина, потоа должината')}</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">{t('coordinatesGuide.format.rule4', 'Не додавајте симболи како °, \', " или NSEW')}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoordinatesGuide;
